import apiService from './apiService';

const API_BASE = '/accounting';

export interface DeferredExpense {
  id: string;
  name: string;
  referenceType: 'VENDOR_BILL' | 'MANUAL_ENTRY';
  referenceId?: string;
  deferredAccountId: string;
  expenseAccountId: string;
  totalAmount: number;
  recognizedAmount: number;
  remainingAmount: number;
  startDate: string;
  endDate: string;
  recognitionMethod: 'STRAIGHT_LINE' | 'CUSTOM';
  frequency: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL';
  state: 'DRAFT' | 'RUNNING' | 'FULLY_RECOGNIZED' | 'CANCELLED';
  recognitionState: 'PENDING' | 'PARTIAL' | 'COMPLETE';
  scheduleLines?: DeferredExpenseScheduleLine[];
  deferredAccount?: any;
  expenseAccount?: any;
  vendorBill?: any;
  initialJournalEntry?: any;
  createdByUser?: any;
  createdAt: string;
  updatedAt: string;
}

export interface DeferredExpenseScheduleLine {
  id: string;
  deferredExpenseId: string;
  periodStartDate: string;
  periodEndDate: string;
  amount: number;
  state: 'DRAFT' | 'POSTED';
  journalEntryId?: string;
  journalEntry?: any;
  postedAt?: string;
  postedBy?: string;
  postedByUser?: any;
}

export interface CreateDeferredExpenseData {
  name: string;
  referenceType: 'VENDOR_BILL' | 'MANUAL_ENTRY';
  referenceId?: string;
  deferredAccountId: string;
  expenseAccountId: string;
  totalAmount: number;
  startDate: string;
  endDate: string;
  recognitionMethod?: 'STRAIGHT_LINE' | 'CUSTOM';
  frequency?: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL';
}

export interface DeferredExpenseFilters {
  state?: 'DRAFT' | 'RUNNING' | 'FULLY_RECOGNIZED' | 'CANCELLED';
  recognitionState?: 'PENDING' | 'PARTIAL' | 'COMPLETE';
  startDateFrom?: string;
  startDateTo?: string;
  referenceType?: 'VENDOR_BILL' | 'MANUAL_ENTRY';
}

const deferredExpenseService = {
  /**
   * Get all deferred expenses with optional filters
   */
  getAll: async (filters?: DeferredExpenseFilters) => {
    const response = await apiService.get(`${API_BASE}/deferred-expenses`, { params: filters });
    return response.data;
  },

  /**
   * Get deferred expense by ID
   */
  getById: async (id: string) => {
    const response = await apiService.get(`${API_BASE}/deferred-expenses/${id}`);
    return response.data;
  },

  /**
   * Create new deferred expense
   */
  create: async (data: CreateDeferredExpenseData) => {
    const response = await apiService.post(`${API_BASE}/deferred-expenses`, data);
    return response.data;
  },

  /**
   * Update deferred expense
   */
  update: async (id: string, data: Partial<CreateDeferredExpenseData>) => {
    const response = await apiService.put(`${API_BASE}/deferred-expenses/${id}`, data);
    return response.data;
  },

  /**
   * Delete deferred expense
   */
  delete: async (id: string) => {
    const response = await apiService.delete(`${API_BASE}/deferred-expenses/${id}`);
    return response.data;
  },

  /**
   * Start recognition (DRAFT → RUNNING)
   */
  startRecognition: async (id: string, journalId: string) => {
    const response = await apiService.post(`${API_BASE}/deferred-expenses/${id}/start`, { journalId });
    return response.data;
  },

  /**
   * Post single schedule line
   */
  postScheduleLine: async (lineId: string, data: { journalId: string; accountingDate?: string }) => {
    const response = await apiService.post(`${API_BASE}/deferred-expenses/schedule-lines/${lineId}/post`, data);
    return response.data;
  },

  /**
   * Batch post multiple schedule lines
   */
  batchPostScheduleLines: async (
    id: string,
    data: { scheduleLineIds: string[]; journalId: string; accountingDate?: string }
  ) => {
    const response = await apiService.post(`${API_BASE}/deferred-expenses/${id}/schedule-lines/batch-post`, data);
    return response.data;
  },

  /**
   * Cancel deferred expense
   */
  cancel: async (id: string, journalId: string, reason: string) => {
    const response = await apiService.post(`${API_BASE}/deferred-expenses/${id}/cancel`, { journalId, reason });
    return response.data;
  },

  /**
   * Get schedule lines for a deferred expense
   */
  getScheduleLines: async (id: string) => {
    const response = await apiService.get(`${API_BASE}/deferred-expenses/${id}/schedule-lines`);
    return response.data;
  },
};

export default deferredExpenseService;
